import React, { useCallback, useContext, useEffect } from 'react';
import { Consumable } from '@/types/database/communityDevice';
import { Box } from '@mui/material';
import { Button, Dialog, Message } from '@alifd/next';
import { DatePicker, Form, Input, InputNumber, Select, Upload } from 'antd';
import locale from 'antd/es/date-picker/locale/zh_TW';
import { VendorContext } from '../..';
import moment from 'moment';
import client from '@/lib/feathers';

import { updateFile } from '../../utils';
const { Item, useForm } = Form;

interface FormValues extends Omit<Consumable, 'deviceDate'> {
  deviceDate: moment.Moment;
}
const EditDialog: React.FC<{
  record: Consumable | undefined;
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setTrigger: React.Dispatch<React.SetStateAction<Record<string, never>>>;
}> = ({ record, setTrigger, visible, setVisible }) => {
  const [form] = useForm<FormValues>();

  const vendors = useContext(VendorContext);

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        ...record,
        deviceDate: moment(record.deviceDate),
        files: Array.isArray(record.files) ? record.files : [],
        specification: Array.isArray(record.specification)
          ? record.specification
          : [],
      });
    } else {
      form.resetFields();
    }
  }, [form, record]);

  const handleFinish = useCallback(
    async (values: FormValues) => {
      const data = {
        ...values,
        createdTime: record?.createdTime || moment().valueOf(),
        deviceDate: values.deviceDate.valueOf(),
        deviceType: '消耗用品',
        deviceNote: values.deviceNote || '',
        files: values.files || [],
        specification: values.specification || [],
      };
      try {
        await updateFile(form, record, ['files', 'specification']);
        if (record) {
          await client.service('communityDevice').patch(record._id, data);
        } else {
          await client.service('communityDevice').create(data);
        }
        Message.success('儲存成功');
        form.resetFields();
        setTrigger({});
        setVisible(false);
      } catch (err) {
        console.error(err);
        Message.error(err.message);
      }
    },
    [form, record, setTrigger, setVisible]
  );

  return (
    <Dialog
      title={record ? '編輯耗材' : '新增耗材'}
      visible={visible}
      onOk={() => form.submit()}
      onCancel={() => setVisible(false)}
      onClose={() => setVisible(false)}
    >
      <Box
        sx={{
          width: '600px',
          height: '600px',
          overflowY: 'auto',
        }}
      >
        <Form
          form={form}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 14 }}
          onFinish={handleFinish}
        >
          <Item
            label="耗材名稱"
            name="deviceName"
            rules={[{ required: true, message: '請輸入耗材名稱' }]}
          >
            <Input maxLength={25} showCount />
          </Item>
          <Item
            label="耗材編號"
            name="deviceNumber"
            rules={[{ required: true, message: '請輸入耗材編號' }]}
          >
            <Input maxLength={25} showCount />
          </Item>
          <Item
            label="保管人"
            name="deviceKeeper"
            rules={[{ required: true, message: '請輸入保管人' }]}
          >
            <Input maxLength={25} showCount />
          </Item>
          <Item
            label="保管位置"
            name="deviceStorePlace"
            rules={[{ required: true, message: '請輸入保管位置' }]}
          >
            <Input maxLength={25} showCount />
          </Item>
          <Item
            label="耗材廠商"
            name="deviceVendor"
            rules={[{ required: true, message: '請輸入耗材廠商' }]}
          >
            <Select allowClear>
              {vendors.map((vendor) => (
                <Select.Option key={vendor._id} value={vendor._id}>
                  {vendor.name}
                </Select.Option>
              ))}
            </Select>
          </Item>
          <Item
            label="耗材價值(元)"
            name="deviceMoney"
            rules={[{ required: true, message: '請輸入耗材價值' }]}
          >
            <InputNumber min={0} />
          </Item>
          <Item
            label="耗材使用年限(年)"
            name="deviceYear"
            rules={[{ required: true, message: '請輸入耗材使用年限' }]}
          >
            <InputNumber min={0} />
          </Item>
          <Item
            label="購入日期"
            name="deviceDate"
            rules={[{ required: true, message: '請輸入購入日期' }]}
          >
            <DatePicker locale={locale} />
          </Item>
          <Item
            label="購買數量"
            name="purchaseQuantity"
            rules={[{ required: true, message: '請輸入購買數量' }]}
          >
            <InputNumber min={0} />
          </Item>
          <Item label="剩餘數量" name="remainingQuantity">
            <InputNumber min={0} />
          </Item>
          <Item label="備註" name="deviceNote">
            <Input.TextArea maxLength={100} showCount />
          </Item>
          <Item
            label="上傳耗材照片"
            name="files"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
          >
            <Upload accept="image/*" beforeUpload={() => false} maxCount={1}>
              <Button>上傳照片</Button>
            </Upload>
          </Item>
          <Item
            label="上傳耗材規範"
            name="specification"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
          >
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              accept=".doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx"
            >
              <Button>上傳規範</Button>
            </Upload>
          </Item>
        </Form>
      </Box>
    </Dialog>
  );
};

export default EditDialog;
